export const WARNING_ONLY_FIELDS = new Set(['Business Entity Name', 'Business Entity Type', 'State of Formation'])

export const SALESFORCE_BASE_URL = import.meta.env.VITE_SALESFORCE_BASE_URL ?? 'https://kingenergy.lightning.force.com'

export const PMA_STATUS_BADGE_MAP: Record<string, { label: string; classes: string }> = {
  SUBMITTED: { label: 'Submitted', classes: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200' },
  ACCEPTED: { label: 'Accepted', classes: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' },
  REJECTED: { label: 'Rejected', classes: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200' },
  EXPIRED: { label: 'Expired', classes: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200' },
  EXPIRING_SOON: { label: 'Expiring', classes: 'bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-200' },
}

const _expiringSoonRaw = Number(import.meta.env.VITE_TENANT_ONBOARDING_PMA_EXPIRING_SOON_DAYS ?? 60)
export const PMA_EXPIRING_SOON_DAYS = Number.isNaN(_expiringSoonRaw) ? 60 : _expiringSoonRaw

const _latestTesaRaw = Number(import.meta.env.VITE_LATEST_TESA_VERSION ?? 6.1)
export const LATEST_TESA_VERSION = Number.isNaN(_latestTesaRaw) ? 6.1 : _latestTesaRaw

/**
 * Classifies a TESA version string into one of three display states:
 *   - isCurrent: matches LATEST exactly (shown as green "Current" badge)
 *   - isLegacy:  prior major version, e.g. v1–v5 when LATEST is 6.x (shown as amber "Legacy" badge)
 *   - neither:   same major but prior minor (e.g. v6 when LATEST is 6.1) — show no badge,
 *                since these aren't the latest but aren't deprecated enough to call "Legacy"
 *   - isCustom:  uploaded TESA with a non-versioned/external template
 *
 * parseFloat (not parseInt) is required so dotted versions like "v6.1" compare correctly.
 */
export function parseTesaVersion(raw: string | null | undefined): {
  display: string
  isLegacy: boolean
  isCurrent: boolean
  isCustom: boolean
} {
  if (raw == null) return { display: '', isLegacy: false, isCurrent: false, isCustom: false }
  if (raw === 'custom') return { display: 'Custom', isLegacy: false, isCurrent: false, isCustom: true }
  const num = parseFloat(raw.replace('v', ''))
  if (Number.isNaN(num)) return { display: raw, isLegacy: false, isCurrent: false, isCustom: false }
  const isCurrent = num === LATEST_TESA_VERSION
  const isLegacy = num < Math.floor(LATEST_TESA_VERSION)
  return { display: raw, isLegacy, isCurrent, isCustom: false }
}

/**
 * Detects KE-internal NGOM/VNEM accounts by tenant business display name. These accounts don't
 * require TESA / PMA documentation, so the onboarding UI:
 *   - tags them with an "NGOM" attribute badge,
 *   - hides the TESA and Utility Authorization (PMA) progress steps,
 *   - and the backend skips the agreement-presence gate when marking complete.
 *
 * Match is case-insensitive whole-word: "Acme NGOM LLC", "VNEM Holdings", and "NGOM" all match;
 * "vNEM2VAccount" or "TURNGOM" do not.
 */
const NGOM_OR_VNEM_NAME_RE = /\b(NGOM|VNEM)\b/i
export function isNgomOrVnemTenantName(name: string | null | undefined): boolean {
  return name != null && NGOM_OR_VNEM_NAME_RE.test(name)
}

/**
 * Detects National tenant accounts from utility-account tenantType. National accounts don't
 * require TESA / PMA documentation, so the onboarding UI hides those progress steps and the
 * backend skips the agreement-presence gate when marking complete.
 */
export function isNationalTenantAccount(
  utilityAccounts: Array<{ tenantType?: string | null }> | null | undefined,
): boolean {
  return utilityAccounts?.some((ua) => ua.tenantType?.toLowerCase() === 'national') ?? false
}

/**
 * Detects the landlord common-area (CAM) account by name ("Common Area - <Property>, <City>").
 * Mirrors backend `property-name.util.ts`. CAM is a structural per-property account that shares the
 * property manager's contact info with its tenants, so it must never be offered as a merge target
 * for a real registration. Backend already excludes it; this is UI defense-in-depth.
 *
 * Whole-word, case-insensitive: "Common Area" matches; "Uncommon Area" does not.
 */
const COMMON_AREA_NAME_RE = /\bcommon area\b/i
export function isCommonAreaTenantName(name: string | null | undefined): boolean {
  return name != null && COMMON_AREA_NAME_RE.test(name)
}

/**
 * King property records use "Name - City, ST" while onboarding feeds often store the short
 * property name only. Mirrors backend `property-name.util.ts` for conflict/merge UX.
 *
 * Splits on " - " (space-hyphen-space), not a bare "-", so property names with internal hyphens
 * ("Stonewood-Pico Rivera") aren't truncated and distinct properties aren't over-collapsed — the
 * cause of false cross-property merge recommendations (WEB-6529 / WEB-6624).
 */
export function propertyBaseName(propertyName: string): string {
  return propertyName.split(' - ')[0]?.trim() ?? ''
}

export function propertyNamesAlign(feedPropertyName: string, otherPropertyName: string): boolean {
  const feed = feedPropertyName.trim()
  const other = otherPropertyName.trim()
  if (feed === '' || other === '') {
    return feed === other
  }
  if (feed.toLowerCase() === other.toLowerCase()) {
    return true
  }
  const feedBase = propertyBaseName(feed)
  const otherBase = propertyBaseName(other)
  return feedBase !== '' && feedBase.toLowerCase() === otherBase.toLowerCase()
}

/** Prefer servicing.property name from preview API; fall back to feed JSON property. */
export function registrationDisplayPropertyName(
  previewPropertyName: string | null | undefined,
  feedPropertyName: string | null | undefined,
): string | undefined {
  const resolved = previewPropertyName?.trim()
  if (resolved != null && resolved !== '') return resolved
  const feed = feedPropertyName?.trim()
  if (feed != null && feed !== '') return feed
  return undefined
}

export function distinctPropertyBaseNames(propertyNames: string[]): string[] {
  return [...new Set(propertyNames.map(propertyBaseName).filter(Boolean))]
}
